import React, { useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import formatCurrency from '../../../utils/formatCurrency';
import { parseDatePreservingCalendar } from '../../../utils/formatDate';
import { AccountingLedgerSource, ApLedgerRecord, ApVendorRecord, ArCustomerRecord, ArLedgerRecord, QboAccount, QboBillRecord, QboGeneralLedgerRecord, QboInvoiceRecord, QboLineItem, QboTransactionListItem, StandardAccount, UploadedLedgerRecord, VendorLineItemRecord } from '../../../api/types';
import { formatApLedgerRecordType, formatApLedgerReference, getApBillListStatus } from '../ap/apFormatters';
import { formatInventoryDisplayName, formatInventoryVendors, getInventoryVendorList } from '../inventory/inventoryFormatters';
import StandardAccountSelector from '../components/StandardAccountSelector';
import { getDocumentVendorName } from '../../../utils/getDocumentVendorName';

export type LedgerDatasetItem = ApLedgerRecord | ApVendorRecord | ArCustomerRecord | QboTransactionListItem | QboGeneralLedgerRecord | QboInvoiceRecord | QboBillRecord | ArLedgerRecord | UploadedLedgerRecord | VendorLineItemRecord | QboAccount;

export interface LedgerDatasetTableShellProps {
    view: 'accounting' | 'ar' | 'inventory';
    accountingSource: AccountingLedgerSource;
    items: LedgerDatasetItem[];
    isLoading: boolean;
    total: number;
    page: number;
    pageSize: number;
    onPageChange: (page: number) => void;
    hasActiveFilters?: boolean;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    defaultSortBy?: string;
    defaultSortOrder?: 'asc' | 'desc';
    suppressDefaultSortIndicator?: boolean;
    onSort?: (column: string) => void;
    onRowClick: (item: LedgerDatasetItem) => void;
    standardAccounts?: StandardAccount[];
    onUpdateGlMapping?: (qboAccountId: string, standardAccountId: string | null) => void;
    isUpdatingGlMapping?: boolean;
    businessId?: string;
}

const formatDate = (value?: string) => {
    if (!value) return '—';
    const parsed = parseDatePreservingCalendar(value);
    if (!parsed) return '—';
    return parsed.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
};

const getLineItemCount = (lineItems?: QboLineItem[]) => lineItems?.length ?? 0;
const getUploadedPriceChangeSummary = (item: UploadedLedgerRecord) => {
    const increases = item.price_increases?.length ?? 0;
    const decreases = item.price_decreases?.length ?? 0;
    const total = increases + decreases;

    if (!total) {
        return null;
    }

    if (increases && decreases) {
        return {
            label: `${total} change${total !== 1 ? 's' : ''}`,
            className: 'bg-amber-100 text-amber-800',
        };
    }

    if (increases) {
        return {
            label: `${increases} increase${increases !== 1 ? 's' : ''}`,
            className: 'bg-rose-100 text-rose-800',
        };
    }

    return {
        label: `${decreases} decrease${decreases !== 1 ? 's' : ''}`,
        className: 'bg-emerald-100 text-emerald-800',
    };
};

const formatStatus = (value?: string) => value ? value.replace(/_/g, ' ') : '—';

const getDisplayName = (account: StandardAccount): string => account.display_name || account.account_name || 'Untitled Account';

const getPaymentMethodBadgeClass = (value?: string | null): string => {
    const normalized = String(value || '').trim().toLowerCase();
    if (normalized === 'ach') return 'bg-indigo-100 text-indigo-800';
    if (normalized === 'check') return 'bg-slate-100 text-slate-700';
    if (normalized === 'zelle') return 'bg-purple-100 text-purple-800';
    if (normalized === 'wire') return 'bg-amber-100 text-amber-800';
    if (normalized === 'card') return 'bg-sky-100 text-sky-800';
    return 'bg-gray-100 text-gray-700';
};

const isLedgerDatasetItem = (item: LedgerDatasetItem | null | undefined): item is LedgerDatasetItem => Boolean(item && typeof item === 'object');

const LedgerDatasetTableShell: React.FC<LedgerDatasetTableShellProps> = ({
    view,
    accountingSource,
    items,
    isLoading,
    total,
    page,
    pageSize,
    onPageChange,
    hasActiveFilters = false,
    sortBy,
    sortOrder,
    defaultSortBy,
    defaultSortOrder,
    suppressDefaultSortIndicator = false,
    onSort,
    onRowClick,
    standardAccounts = [],
    onUpdateGlMapping,
    isUpdatingGlMapping = false,
    businessId,
}) => {
    const [selectorAccount, setSelectorAccount] = useState<QboAccount | null>(null);
    const [pageInput, setPageInput] = useState(String(page));
    const [isEditingPage, setIsEditingPage] = useState(false);
    const displayItems = useMemo(() => {
        if (accountingSource !== 'ar_ledger' && accountingSource !== 'ap_ledger') {
            return items;
        }
        if (accountingSource === 'ar_ledger') {
            return items.filter((item) => isLedgerDatasetItem(item) && (item as ArLedgerRecord).record_type !== 'exception');
        }
        if (accountingSource === 'ap_ledger') {
            return items.filter((item) => {
                if (!isLedgerDatasetItem(item)) return false;
                const recordType = String((item as ApLedgerRecord).record_type || '').trim().toLowerCase();
                return recordType !== 'exception' && recordType !== 'bank_outflow';
            });
        }
        return items;
    }, [accountingSource, items]);
    const standardAccountById = useMemo(
        () => new Map(standardAccounts.map((account) => [account._id, account])),
        [standardAccounts],
    );

    useEffect(() => {
        setPageInput(String(page));
        setIsEditingPage(false);
    }, [page]);

    if (isLoading) {
        return <div className="p-8 text-center text-gray-500">Loading ledger data...</div>;
    }

    const safeItems = displayItems.filter(isLedgerDatasetItem);
    const totalPages = Math.ceil(total / pageSize);
    const accountingColSpan = accountingSource === 'transaction_list'
        ? 8
        : accountingSource === 'general_ledger'
            ? 9
            : accountingSource === 'ar_customers'
                ? 9
            : accountingSource === 'ap_vendors'
                ? 9
            : accountingSource === 'ap_ledger'
                ? 9
            : accountingSource === 'ar_ledger'
                ? 9
            : (accountingSource === 'uploaded_invoices' || accountingSource === 'uploaded_bills')
                ? 9
                : accountingSource === 'uploaded_expenses'
                    ? 8
                    : accountingSource === 'gl_code'
                        ? 7
                        : 7;

    const renderSortIcon = (column: string) => {
        const isDefaultSort = suppressDefaultSortIndicator && sortBy === defaultSortBy && sortOrder === defaultSortOrder;
        if (isDefaultSort && column === defaultSortBy) {
            return <span className="inline-flex w-3 justify-center text-gray-300"></span>;
        }
        if (sortBy !== column) {
            return <span className="inline-flex w-3 justify-center text-gray-300"></span>;
        }
        return (
            <span className="inline-flex w-3 justify-center text-gray-700">
                {sortOrder === 'asc' ? '↑' : '↓'}
            </span>
        );
    };

    const renderSortableHeader = (
        label: string,
        column: string,
        className: string,
        align: 'left' | 'right' | 'center' = 'left',
    ) => (
        <th
            className={`${className} ${onSort ? 'cursor-pointer hover:bg-gray-50 select-none' : ''}`}
            onClick={onSort ? () => onSort(column) : undefined}
        >
            <div className={`inline-flex items-center gap-1 ${align === 'right' ? 'justify-end w-full' : align === 'center' ? 'justify-center w-full' : ''}`}>
                <span>{label}</span>
                {renderSortIcon(column)}
            </div>
        </th>
    );

    const renderAccountingHeaders = () => {
        if (accountingSource === 'transaction_list') {
            return (
                <tr className="bg-white border-b border-gray-200 text-[#4C545F] font-inter font-medium text-[14px] leading-[120%]">
                    {renderSortableHeader('Date', 'transaction_date', 'p-4 w-[12%]')}
                    {renderSortableHeader('Type', 'transaction_type', 'p-4 w-[14%]')}
                    {renderSortableHeader('Counterparty', 'counterparty', 'p-4 w-[16%]')}
                    {renderSortableHeader('Document #', 'document_number', 'p-4 w-[11%]')}
                    {renderSortableHeader('Account', 'account', 'p-4 w-[13%]')}
                    {renderSortableHeader('Split Account', 'split_account', 'p-4 w-[13%]')}
                    {renderSortableHeader('Memo', 'memo', 'p-4 w-[13%]')}
                    {renderSortableHeader('Amount', 'amount', 'p-4 pr-6 w-[8%] sticky right-0 bg-white z-20 shadow-[-6px_0_12px_-12px_rgba(15,23,42,0.35)]')}
                </tr>
            );
        }

        if (accountingSource === 'gl_code') {
            return (
                <tr className="bg-white border-b border-gray-200 text-[#4C545F] font-inter font-medium text-[14px] leading-[120%]">
                    <th className="p-4 w-[14%]">Group</th>
                    <th className="p-4 w-[22%]">QBO Account</th>
                    <th className="p-4 w-[12%]">Type</th>
                    <th className="p-4 w-[12%]">Classification</th>
                    <th className="p-4 w-[22%]">Finz Account</th>
                </tr>
            );
        }

        if (accountingSource === 'general_ledger') {
            return (
                <tr className="bg-white border-b border-gray-200 text-[#4C545F] font-inter font-medium text-[14px] leading-[120%]">
                    {renderSortableHeader('Date', 'transaction_date', 'p-4 w-[12%]')}
                    {renderSortableHeader('Type', 'transaction_type', 'p-4 w-[12%]')}
                    {renderSortableHeader('Document #', 'document_number', 'p-4 w-[12%]')}
                    {renderSortableHeader('Name', 'name', 'p-4 w-[14%]')}
                    {renderSortableHeader('Account', 'account_name', 'p-4 w-[14%]')}
                    {renderSortableHeader('Split Account', 'split_account', 'p-4 w-[14%]')}
                    {renderSortableHeader('Memo', 'memo', 'p-4 w-[14%]')}
                    {renderSortableHeader('Debit', 'debit', 'p-4 w-[8%]')}
                    {renderSortableHeader('Credit', 'credit', 'p-4 pr-6 w-[8%] sticky right-0 bg-white z-20 shadow-[-6px_0_12px_-12px_rgba(15,23,42,0.35)]')}
                </tr>
            );
        }

        if (accountingSource === 'ar_customers') {
            return (
                <tr className="bg-white border-b border-gray-200 text-[#4C545F] font-inter font-medium text-[14px] leading-[120%]">
                    {renderSortableHeader('Customer', 'customer', 'p-4 w-[22%]')}
                    {renderSortableHeader('Open Invoices', 'open_invoice_count', 'p-4 w-[10%]')}
                    {renderSortableHeader('Total Invoiced', 'total_invoiced_amount', 'p-4 w-[12%]')}
                    {renderSortableHeader('Paid / Closed', 'paid_amount_total', 'p-4 w-[12%]')}
                    {renderSortableHeader('Open AR', 'open_ar', 'p-4 w-[12%]')}
                    {renderSortableHeader('Overdue AR', 'overdue_ar', 'p-4 w-[12%]')}
                    {renderSortableHeader('Partial 30d', 'partial_payment_frequency', 'p-4 w-[10%]')}
                    {renderSortableHeader('Risk', 'risk_level', 'p-4 w-[10%]')}
                    {renderSortableHeader('Share of Open AR', 'concentration_share', 'p-4 pr-6 w-[12%] sticky right-0 bg-white z-20 shadow-[-6px_0_12px_-12px_rgba(15,23,42,0.35)]')}
                </tr>
            );
        }

        if (accountingSource === 'ap_vendors') {
            return (
                <tr className="bg-white border-b border-gray-200 text-[#4C545F] font-inter font-medium text-[14px] leading-[120%]">
                    {renderSortableHeader('Vendor', 'vendor', 'p-4 w-[16%]')}
                    {renderSortableHeader('Due', 'next_due_date', 'p-4 w-[10%]')}
                    {renderSortableHeader('Frequency', 'primary_frequency_label', 'p-4 w-[11%]')}
                    {renderSortableHeader('Bucket', 'primary_accounting_category', 'p-4 w-[11%]')}
                    {renderSortableHeader('Chart of Account', 'primary_accounting_account_name', 'p-4 w-[15%]')}
                    {renderSortableHeader('Pay By', 'preferred_payment_method', 'p-4 w-[9%]')}
                    {renderSortableHeader('Open AP', 'open_ap', 'p-4 w-[11%]')}
                    {renderSortableHeader('Overdue AP', 'overdue_ap', 'p-4 w-[11%]')}
                    {renderSortableHeader('Share of Open AP', 'concentration_share', 'p-4 pr-6 w-[11%]')}
                </tr>
            );
        }

        if (accountingSource === 'ar_ledger') {
            return (
                <tr className="bg-white border-b border-gray-200 text-[#4C545F] font-inter font-medium text-[14px] leading-[120%]">
                    {renderSortableHeader('Event Date', 'event_date', 'p-4 w-[12%]')}
                    {renderSortableHeader('Record Type', 'record_type', 'p-4 w-[12%]')}
                    {renderSortableHeader('Customer', 'customer', 'p-4 w-[18%]')}
                    {renderSortableHeader('Reference', 'reference_no', 'p-4 w-[14%]')}
                    {renderSortableHeader('Due Date', 'due_date', 'p-4 w-[12%]')}
                    {renderSortableHeader('Amount', 'amount', 'p-4 w-[10%]')}
                    {renderSortableHeader('Open Amount', 'open_amount', 'p-4 w-[10%]')}
                    {renderSortableHeader('Status', 'status', 'p-4 w-[12%]')}
                    {renderSortableHeader('Reconciliation', 'reconciliation_status', 'p-4 pr-6 w-[12%]')}
                </tr>
            );
        }

        if (accountingSource === 'ap_ledger') {
            return (
                <tr className="bg-white border-b border-gray-200 text-[#4C545F] font-inter font-medium text-[14px] leading-[120%]">
                    {renderSortableHeader('Event Date', 'event_date', 'p-4 w-[11%]')}
                    {renderSortableHeader('Record Type', 'record_type', 'p-4 w-[12%]')}
                    {renderSortableHeader('Vendor', 'vendor', 'p-4 w-[17%]')}
                    {renderSortableHeader('Reference', 'reference_no', 'p-4 w-[12%]')}
                    {renderSortableHeader('Due Date', 'due_date', 'p-4 w-[10%]')}
                    {renderSortableHeader('Expected Payment Date', 'expected_payment_date', 'p-4 w-[12%]')}
                    {renderSortableHeader('Amount', 'amount', 'p-4 w-[8%]')}
                    {renderSortableHeader('Open Amount', 'open_amount', 'p-4 w-[8%]')}
                    {renderSortableHeader('Status', 'status', 'p-4 w-[10%]')}
                    {renderSortableHeader('Reconciliation', 'reconciliation_status', 'p-4 pr-6 w-[10%]')}
                </tr>
            );
        }

        if (accountingSource === 'invoices') {
            return (
                <tr className="bg-white border-b border-gray-200 text-[#4C545F] font-inter font-medium text-[14px] leading-[120%]">
                    {renderSortableHeader('Date', 'transaction_date', 'p-4 w-[14%]')}
                    {renderSortableHeader('Customer', 'customer', 'p-4 w-[24%]')}
                    {renderSortableHeader('Invoice #', 'invoice_number', 'p-4 w-[14%]')}
                    {renderSortableHeader('Due Date', 'due_date', 'p-4 w-[14%]')}
                    <th className="p-4 w-[12%]">Line Items</th>
                    {renderSortableHeader('Total', 'total_amount', 'p-4 w-[12%]')}
                    {renderSortableHeader('Balance Due', 'balance_due', 'p-4 pr-6 w-[10%] sticky right-0 bg-white z-20 shadow-[-6px_0_12px_-12px_rgba(15,23,42,0.35)]')}
                </tr>
            );
        }

        if (accountingSource === 'uploaded_invoices') {
            return (
                <tr className="bg-white border-b border-gray-200 text-[#4C545F] font-inter font-medium text-[14px] leading-[120%]">
                    {renderSortableHeader('Date', 'transaction_date', 'p-4 w-[14%]')}
                    {renderSortableHeader('Vendor', 'vendor', 'p-4 w-[20%]')}
                    {renderSortableHeader('Invoice #', 'invoice_number', 'p-4 w-[12%]')}
                    {renderSortableHeader('Due Date', 'due_date', 'p-4 w-[14%]')}
                    <th className="p-4 w-[12%]">Line Items</th>
                    <th className="p-4 w-[10%] text-center">Price</th>
                    {renderSortableHeader('Total', 'total_amount', 'p-4 w-[10%]')}
                    {renderSortableHeader('Status', 'status', 'p-4 w-[10%]')}
                    {renderSortableHeader('Balance Due', 'balance_due', 'p-4 pr-6 w-[10%] sticky right-0 bg-white z-20 shadow-[-6px_0_12px_-12px_rgba(15,23,42,0.35)]')}
                </tr>
            );
        }

        if (accountingSource === 'uploaded_expenses') {
            return (
                <tr className="bg-white border-b border-gray-200 text-[#4C545F] font-inter font-medium text-[14px] leading-[120%]">
                    {renderSortableHeader('Date', 'transaction_date', 'p-4 w-[14%]')}
                    {renderSortableHeader('Vendor', 'vendor', 'p-4 w-[22%]')}
                    {renderSortableHeader('Reference #', 'invoice_number', 'p-4 w-[12%]')}
                    {renderSortableHeader('Due Date', 'due_date', 'p-4 w-[14%]')}
                    <th className="p-4 w-[12%]">Line Items</th>
                    {renderSortableHeader('Total', 'total_amount', 'p-4 w-[12%]')}
                    {renderSortableHeader('Status', 'status', 'p-4 w-[14%]')}
                    {renderSortableHeader('Balance Due', 'balance_due', 'p-4 pr-6 w-[10%] sticky right-0 bg-white z-20 shadow-[-6px_0_12px_-12px_rgba(15,23,42,0.35)]')}
                </tr>
            );
        }

        if (accountingSource === 'bills') {
            return (
                <tr className="bg-white border-b border-gray-200 text-[#4C545F] font-inter font-medium text-[14px] leading-[120%]">
                    {renderSortableHeader('Date', 'transaction_date', 'p-4 w-[14%]')}
                    {renderSortableHeader('Vendor', 'vendor', 'p-4 w-[30%]')}
                    {renderSortableHeader('Bill #', 'bill_id', 'p-4 w-[16%]')}
                    {renderSortableHeader('Due Date', 'due_date', 'p-4 w-[14%]')}
                    {renderSortableHeader('Status', 'status', 'p-4 w-[12%]')}
                    {renderSortableHeader('Total', 'total_amount', 'p-4 w-[10%]')}
                    {renderSortableHeader('Balance Due', 'balance_due', 'p-4 pr-6 w-[14%] sticky right-0 bg-white z-20 shadow-[-6px_0_12px_-12px_rgba(15,23,42,0.35)]')}
                </tr>
            );
        }

        return (
            <tr className="bg-white border-b border-gray-200 text-[#4C545F] font-inter font-medium text-[14px] leading-[120%]">
                {renderSortableHeader('Date', 'transaction_date', 'p-4 w-[14%]')}
                {renderSortableHeader('Vendor', 'vendor', 'p-4 w-[20%]')}
                {renderSortableHeader('Bill #', 'invoice_number', 'p-4 w-[12%]')}
                {renderSortableHeader('Due Date', 'due_date', 'p-4 w-[14%]')}
                <th className="p-4 w-[12%]">Line Items</th>
                <th className="p-4 w-[10%] text-center">Price</th>
                {renderSortableHeader('Total', 'total_amount', 'p-4 w-[10%]')}
                {renderSortableHeader('Status', 'status', 'p-4 w-[10%]')}
                {renderSortableHeader('Balance Due', 'balance_due', 'p-4 pr-6 w-[10%] sticky right-0 bg-white z-20 shadow-[-6px_0_12px_-12px_rgba(15,23,42,0.35)]')}
            </tr>
        );
    };

    const renderInventoryHeaders = () => (
        <tr className="bg-white border-b border-gray-200 text-[#4C545F] font-inter font-medium text-[14px] leading-[120%]">
            {renderSortableHeader('Item', 'description', 'p-4 w-[26%]')}
            {renderSortableHeader('Vendors', 'vendor_count', 'p-4 w-[14%]')}
            {renderSortableHeader('Most Recent', 'most_recent_unit_price', 'p-4 w-[12%]')}
            {renderSortableHeader('Lowest', 'lowest_unit_price', 'p-4 w-[12%]')}
            {renderSortableHeader('Invoices#', 'observation_count', 'p-4 w-[8%]')}
            {renderSortableHeader('Last Updated', 'most_recent_seen_at', 'p-4 pr-6 w-[14%] sticky right-0 bg-white z-20 shadow-[-6px_0_12px_-12px_rgba(15,23,42,0.35)]')}
        </tr>
    );

    const renderAccountingRows = () => {
        if (accountingSource === 'transaction_list') {
            return (safeItems as QboTransactionListItem[]).map((item, index) => (
                <tr key={item._id ?? `transaction-list-${index}`} onClick={() => onRowClick(item)} className="hover:bg-gray-50 transition-colors cursor-pointer group">
                    <td className="p-4 text-[14px] font-medium text-gray-500 whitespace-nowrap">{formatDate(item.data?.transaction_date)}</td>
                    <td className="p-4 text-[14px] text-gray-900">{item.data?.transaction_type?.name || '—'}</td>
                    <td className="p-4 text-[14px] text-gray-900 truncate group-hover:text-indigo-600 transition-colors" title={item.data?.counterparty?.name || item.data?.customer?.name || item.data?.vendor?.name || '—'}>
                        {item.data?.counterparty?.name || item.data?.customer?.name || item.data?.vendor?.name || '—'}
                    </td>
                    <td className="p-4 text-[14px] text-gray-500">{item.data?.document_number || '—'}</td>
                    <td className="p-4 text-[14px] text-gray-500 truncate" title={item.data?.account?.name || '—'}>
                        {item.data?.account?.name || '—'}
                    </td>
                    <td className="p-4 text-[14px] text-gray-500 truncate" title={item.data?.split_account?.name || '—'}>
                        {item.data?.split_account?.name || '—'}
                    </td>
                    <td className="p-4 text-[14px] text-gray-500 truncate" title={item.data?.memo || '—'}>
                        {item.data?.memo || '—'}
                    </td>
                    <td className="p-4 pr-6 text-[14px] font-medium text-gray-900 sticky right-0 bg-white z-10 shadow-[-6px_0_12px_-12px_rgba(15,23,42,0.35)]">
                        {item.data?.amount_missing && !item.data?.amount_derived_from_journal_lines
                            ? '—'
                            : formatCurrency(item.data?.amount, item.data?.currency || 'USD')}
                    </td>
                </tr>
            ));
        }

        if (accountingSource === 'gl_code') {
            return (safeItems as QboAccount[]).map((item, index) => (
                <tr key={item._id ?? `gl-code-${index}`} className="hover:bg-gray-50 transition-colors group">
                    <td className="p-4 text-[14px] font-medium text-gray-500 whitespace-nowrap">{item.data.account_type || 'Unknown'}</td>
                    <td className="p-4">
                        <div className="text-[14px] text-gray-900 truncate group-hover:text-indigo-600 transition-colors" title={item.data.fully_qualified_name || item.data.name || '—'}>
                            {item.data.fully_qualified_name || item.data.name || '—'}
                        </div>
                        <div className="mt-1 text-xs font-mono text-gray-500">{item.data.account_id || '—'}</div>
                    </td>
                    <td className="p-4 text-[14px] text-gray-500">{item.data.sub_type || item.data.account_type || '—'}</td>
                    <td className="p-4 text-[14px] text-gray-500">{item.data.classification || '—'}</td>
                    <td className="p-4">
                        <button
                            type="button"
                            onClick={() => setSelectorAccount(item)}
                            disabled={isUpdatingGlMapping}
                            className="block w-full min-w-[250px] rounded-md border border-gray-300 bg-white px-3 py-2 text-left text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 disabled:bg-gray-50 disabled:text-gray-400"
                        >
                            {item.mapping?.standard_account_id
                                ? (() => {
                                    const matched = standardAccountById.get(item.mapping?.standard_account_id || '');
                                    if (!matched) return 'Unknown Finz account';
                                    return `${matched.account_number ? `${matched.account_number} · ` : ''}${getDisplayName(matched)}`;
                                })()
                                : 'Select Finz account'}
                        </button>
                    </td>
                </tr>
            ));
        }

        if (accountingSource === 'general_ledger') {
            return (safeItems as QboGeneralLedgerRecord[]).map((item, index) => (
                <tr key={item._id ?? `general-ledger-${index}`} onClick={() => onRowClick(item)} className="hover:bg-gray-50 transition-colors cursor-pointer group">
                    <td className="p-4 text-[14px] font-medium text-gray-500 whitespace-nowrap">{formatDate(item.transaction_date)}</td>
                    <td className="p-4 text-[14px] text-gray-900">{item.transaction_type || '—'}</td>
                    <td className="p-4 text-[14px] text-gray-500">{item.document_number || '—'}</td>
                    <td className="p-4 text-[14px] text-gray-900 truncate group-hover:text-indigo-600 transition-colors" title={item.name || '—'}>
                        {item.name || '—'}
                    </td>
                    <td className="p-4 text-[14px] text-gray-500 truncate" title={item.account_name || '—'}>
                        {item.account_name || '—'}
                    </td>
                    <td className="p-4 text-[14px] text-gray-500 truncate" title={item.split_account || '—'}>
                        {item.split_account || '—'}
                    </td>
                    <td className="p-4 text-[14px] text-gray-500 truncate" title={item.memo || '—'}>
                        {item.memo || '—'}
                    </td>
                    <td className="p-4 text-[14px] font-medium text-gray-900">
                        {item.debit ? formatCurrency(item.debit, item.currency || 'USD') : '—'}
                    </td>
                    <td className="p-4 pr-6 text-[14px] font-medium text-gray-900 sticky right-0 bg-white z-10 shadow-[-6px_0_12px_-12px_rgba(15,23,42,0.35)]">
                        {item.credit ? formatCurrency(item.credit, item.currency || 'USD') : '—'}
                    </td>
                </tr>
            ));
        }

        if (accountingSource === 'ar_customers') {
            return (safeItems as ArCustomerRecord[]).map((item, index) => (
                <tr
                    key={item._id ?? item.customer_id ?? `ar-customer-${index}`}
                    onClick={() => onRowClick(item)}
                    className="hover:bg-gray-50 transition-colors group cursor-pointer"
                >
                    <td className="p-4 text-[14px] text-gray-900 truncate group-hover:text-indigo-600 transition-colors" title={item.customer_name || '—'}>
                        {item.customer_name || '—'}
                    </td>
                    <td className="p-4 text-[14px] text-gray-500">{item.open_invoice_count ?? '—'}</td>
                    <td className="p-4 text-[14px] font-medium text-gray-900">{formatCurrency(item.total_invoiced_amount, 'USD')}</td>
                    <td className="p-4 text-[14px] font-medium text-gray-900">{formatCurrency(item.paid_amount_total, 'USD')}</td>
                    <td className="p-4 text-[14px] font-medium text-gray-900">{formatCurrency(item.open_ar, 'USD')}</td>
                    <td className="p-4 text-[14px] font-medium text-gray-900">{formatCurrency(item.overdue_ar, 'USD')}</td>
                    <td className="p-4 text-[14px] text-gray-500">{item.partial_payment_frequency ?? 0}</td>
                    <td className="p-4 text-[14px]">
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-700 capitalize whitespace-nowrap">
                            {formatStatus(item.collection_risk_level)}
                        </span>
                    </td>
                    <td className="p-4 pr-6 text-[14px] text-gray-500">
                        {typeof item.concentration_share === 'number' ? `${(item.concentration_share * 100).toFixed(1)}%` : '—'}
                    </td>
                </tr>
            ));
        }

        if (accountingSource === 'ap_vendors') {
            return (safeItems as ApVendorRecord[]).map((item, index) => (
                <tr
                    key={item._id ?? item.ap_vendor_id ?? `ap-vendor-${index}`}
                    onClick={() => onRowClick(item)}
                    className="hover:bg-gray-50 transition-colors group cursor-pointer"
                >
                    <td className="p-4 text-[14px] text-gray-900 truncate whitespace-nowrap group-hover:text-indigo-600 transition-colors" title={item.vendor_name || '—'}>
                        {item.vendor_name || '—'}
                    </td>
                    <td className="p-4 text-[14px] text-gray-500 whitespace-nowrap">
                        {formatDate(item.next_due_date || undefined)}
                    </td>
                    <td className="p-4 text-[14px] text-gray-700 whitespace-nowrap">
                        {item.primary_frequency_label || '—'}
                    </td>
                    <td className="p-4 text-[14px]">
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-700 capitalize whitespace-nowrap">
                            {item.resolved_primary_accounting_category || item.primary_accounting_category || item.next_accounting_category || '—'}
                        </span>
                    </td>
                    <td className="p-4 text-[14px] text-gray-700 truncate whitespace-nowrap" title={item.resolved_primary_accounting_account_name || item.primary_accounting_account_name || item.next_accounting_account_name || '—'}>
                        {item.resolved_primary_accounting_account_name || item.primary_accounting_account_name || item.next_accounting_account_name || '—'}
                    </td>
                    <td className="p-4 text-[14px]">
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap ${getPaymentMethodBadgeClass(item.preferred_payment_method)}`}>
                            {item.preferred_payment_method_label || 'Bank'}
                        </span>
                    </td>
                    <td className="p-4 text-[14px] font-medium text-gray-900">
                        {typeof item.open_ap === 'number'
                            ? formatCurrency(item.open_ap, 'USD')
                            : typeof item.open_bill_amount === 'number'
                                ? formatCurrency(item.open_bill_amount, 'USD')
                                : '—'}
                    </td>
                    <td className="p-4 text-[14px] font-medium text-gray-900">
                        {typeof item.overdue_ap === 'number'
                            ? formatCurrency(item.overdue_ap, 'USD')
                            : '—'}
                    </td>
                    <td className="p-4 pr-6 text-[14px] text-gray-500">
                        {typeof item.concentration_share === 'number' ? `${(item.concentration_share * 100).toFixed(1)}%` : '—'}
                    </td>
                </tr>
            ));
        }

        if (accountingSource === 'ar_ledger') {
            return (safeItems as ArLedgerRecord[]).map((item, index) => (
                <tr
                    key={item.ledger_row_id ?? item._id ?? `ar-ledger-${index}`}
                    onClick={() => onRowClick(item)}
                    className="hover:bg-gray-50 transition-colors group cursor-pointer"
                >
                    <td className="p-4 text-[14px] font-medium text-gray-500 whitespace-nowrap">{formatDate(item.event_date)}</td>
                    <td className="p-4 text-[14px] text-gray-900">{formatApLedgerRecordType(item)}</td>
                    <td className="p-4 text-[14px] text-gray-900 truncate group-hover:text-indigo-600 transition-colors" title={item.customer_name || '—'}>
                        {item.customer_name || '—'}
                    </td>
                    <td className="p-4 text-[14px] text-gray-500 truncate" title={item.reference_no || '—'}>
                        {item.reference_no || '—'}
                    </td>
                    <td className="p-4 text-[14px] text-gray-500 whitespace-nowrap">{formatDate(item.due_date || undefined)}</td>
                    <td className="p-4 text-[14px] font-medium text-gray-900">{formatCurrency(item.amount, 'USD')}</td>
                    <td className="p-4 text-[14px] font-medium text-gray-900">{formatCurrency(item.open_amount, 'USD')}</td>
                    <td className="p-4 text-[14px]">
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-700 capitalize whitespace-nowrap">
                            {formatStatus(item.status)}
                        </span>
                    </td>
                    <td className="p-4 pr-6 text-[14px] text-gray-500">
                        {formatStatus(item.reconciliation_status)}
                    </td>
                </tr>
            ));
        }

        if (accountingSource === 'ap_ledger') {
            return (safeItems as ApLedgerRecord[]).map((item, index) => {
                return (
                    <tr
                        key={item.ledger_row_id ?? item._id ?? `ap-ledger-${index}`}
                        onClick={() => onRowClick(item)}
                        className="hover:bg-gray-50 transition-colors group cursor-pointer"
                    >
                        <td className="p-4 text-[14px] font-medium text-gray-500 whitespace-nowrap">{formatDate(item.event_date)}</td>
                        <td className="p-4 text-[14px] text-gray-900">{formatApLedgerRecordType(item)}</td>
                        <td className="p-4 text-[14px] text-gray-900 truncate group-hover:text-indigo-600 transition-colors" title={item.vendor_name || '—'}>
                            {item.vendor_name || '—'}
                        </td>
                        <td className="p-4 text-[14px] text-gray-500 truncate" title={formatApLedgerReference(item)}>
                            {formatApLedgerReference(item)}
                        </td>
                        <td className="p-4 text-[14px] text-gray-500 whitespace-nowrap">{formatDate(item.due_date || undefined)}</td>
                        <td className="p-4 text-[14px] text-gray-500 whitespace-nowrap">{formatDate(item.expected_payment_date || undefined)}</td>
                        <td className="p-4 text-[14px] font-medium text-gray-900">{formatCurrency(item.amount, 'USD')}</td>
                        <td className="p-4 text-[14px] font-medium text-gray-900">{formatCurrency(item.open_amount, 'USD')}</td>
                        <td className="p-4 text-[14px]">
                            <span className="inline-flex w-fit items-center px-2.5 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-700 capitalize whitespace-nowrap">
                                {formatStatus(item.status)}
                            </span>
                        </td>
                        <td className="p-4 pr-6 text-[14px] text-gray-500">
                            {formatStatus(item.reconciliation_status)}
                        </td>
                    </tr>
                );
            });
        }

        if (accountingSource === 'invoices') {
            return (safeItems as QboInvoiceRecord[]).map((item, index) => (
                <tr key={item._id ?? `invoice-${index}`} onClick={() => onRowClick(item)} className="hover:bg-gray-50 transition-colors cursor-pointer group">
                    <td className="p-4 text-[14px] font-medium text-gray-500 whitespace-nowrap">{formatDate(item.data?.transaction_date)}</td>
                    <td className="p-4 text-[14px] text-gray-900 truncate group-hover:text-indigo-600 transition-colors" title={item.data?.customer?.name || '—'}>
                        {item.data?.customer?.name || '—'}
                    </td>
                    <td className="p-4 text-[14px] text-gray-500">{item.data?.invoice_number || item.data?.invoice_id || '—'}</td>
                    <td className="p-4 text-[14px] text-gray-500">{formatDate(item.data?.due_date)}</td>
                    <td className="p-4 text-[14px] text-gray-500">{getLineItemCount(item.data?.line_items)}</td>
                    <td className="p-4 text-[14px] font-medium text-gray-900">
                        {formatCurrency(item.data?.total_amount, item.data?.currency || 'USD')}
                    </td>
                    <td className="p-4 pr-6 text-[14px] font-medium text-gray-900 sticky right-0 bg-white z-10 shadow-[-6px_0_12px_-12px_rgba(15,23,42,0.35)]">
                        {formatCurrency(item.data?.balance_due, item.data?.currency || 'USD')}
                    </td>
                </tr>
            ));
        }

        if (accountingSource === 'uploaded_invoices') {
            return (safeItems as UploadedLedgerRecord[]).map((item, index) => {
                const priceChangeSummary = getUploadedPriceChangeSummary(item);
                return (
                    <tr key={item._id ?? `uploaded-invoice-${index}`} onClick={() => onRowClick(item)} className="hover:bg-gray-50 transition-colors cursor-pointer group">
                        <td className="p-4 text-[14px] font-medium text-gray-500 whitespace-nowrap">{formatDate(item.data?.transaction_date)}</td>
                        <td className="p-4 text-[14px] text-gray-900 truncate group-hover:text-indigo-600 transition-colors" title={getDocumentVendorName(item, '—') || '—'}>
                            {getDocumentVendorName(item, '—') || '—'}
                        </td>
                        <td className="p-4 text-[14px] text-gray-500">{item.data?.invoice_number || '—'}</td>
                        <td className="p-4 text-[14px] text-gray-500">{formatDate(item.data?.due_date)}</td>
                        <td className="p-4 text-[14px] text-gray-500">{getLineItemCount(item.data?.line_items)}</td>
                        <td className="p-4 text-[14px] text-center">
                            {priceChangeSummary ? (
                                <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium whitespace-nowrap ${priceChangeSummary.className}`}>
                                    {priceChangeSummary.label}
                                </span>
                            ) : (
                                <span className="text-xs text-gray-400">None</span>
                            )}
                        </td>
                        <td className="p-4 text-[14px] font-medium text-gray-900">
                            {formatCurrency(item.data?.total_amount, item.data?.currency || 'USD')}
                        </td>
                        <td className="p-4 text-[14px]">
                            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800 capitalize whitespace-nowrap">
                                {formatStatus(item.data?.status)}
                            </span>
                        </td>
                        <td className="p-4 pr-6 text-[14px] font-medium text-gray-900 sticky right-0 bg-white z-10 shadow-[-6px_0_12px_-12px_rgba(15,23,42,0.35)]">
                            {formatCurrency(item.data?.balance_due, item.data?.currency || 'USD')}
                        </td>
                    </tr>
                );
            });
        }

        if (accountingSource === 'uploaded_bills') {
            return (safeItems as UploadedLedgerRecord[]).map((item, index) => {
                const priceChangeSummary = getUploadedPriceChangeSummary(item);
                return (
                    <tr key={item._id ?? `uploaded-bill-${index}`} onClick={() => onRowClick(item)} className="hover:bg-gray-50 transition-colors cursor-pointer group">
                        <td className="p-4 text-[14px] font-medium text-gray-500 whitespace-nowrap">{formatDate(item.data?.transaction_date)}</td>
                        <td className="p-4 text-[14px] text-gray-900 truncate group-hover:text-indigo-600 transition-colors" title={getDocumentVendorName(item, '—') || '—'}>
                            {getDocumentVendorName(item, '—') || '—'}
                        </td>
                        <td className="p-4 text-[14px] text-gray-500">{item.data?.invoice_number || '—'}</td>
                        <td className="p-4 text-[14px] text-gray-500">{formatDate(item.data?.due_date)}</td>
                        <td className="p-4 text-[14px] text-gray-500">{getLineItemCount(item.data?.line_items)}</td>
                        <td className="p-4 text-[14px] text-center">
                            {priceChangeSummary ? (
                                <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium whitespace-nowrap ${priceChangeSummary.className}`}>
                                    {priceChangeSummary.label}
                                </span>
                            ) : (
                                <span className="text-xs text-gray-400">None</span>
                            )}
                        </td>
                        <td className="p-4 text-[14px] font-medium text-gray-900">
                            {formatCurrency(item.data?.total_amount, item.data?.currency || 'USD')}
                        </td>
                        <td className="p-4 text-[14px]">
                            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800 capitalize whitespace-nowrap">
                                {formatStatus(item.data?.status)}
                            </span>
                        </td>
                        <td className="p-4 pr-6 text-[14px] font-medium text-gray-900 sticky right-0 bg-white z-10 shadow-[-6px_0_12px_-12px_rgba(15,23,42,0.35)]">
                            {formatCurrency(item.data?.balance_due, item.data?.currency || 'USD')}
                        </td>
                    </tr>
                );
            });
        }

        if (accountingSource === 'uploaded_expenses') {
            return (safeItems as UploadedLedgerRecord[]).map((item, index) => (
                <tr key={item._id ?? `uploaded-expense-${index}`} onClick={() => onRowClick(item)} className="hover:bg-gray-50 transition-colors cursor-pointer group">
                    <td className="p-4 text-[14px] font-medium text-gray-500 whitespace-nowrap">{formatDate(item.data?.transaction_date)}</td>
                    <td className="p-4 text-[14px] text-gray-900 truncate group-hover:text-indigo-600 transition-colors" title={getDocumentVendorName(item, '—') || '—'}>
                        {getDocumentVendorName(item, '—') || '—'}
                    </td>
                    <td className="p-4 text-[14px] text-gray-500">{item.data?.invoice_number || '—'}</td>
                    <td className="p-4 text-[14px] text-gray-500">{formatDate(item.data?.due_date)}</td>
                    <td className="p-4 text-[14px] text-gray-500">{getLineItemCount(item.data?.line_items)}</td>
                    <td className="p-4 text-[14px] font-medium text-gray-900">
                        {formatCurrency(item.data?.total_amount, item.data?.currency || 'USD')}
                    </td>
                    <td className="p-4 text-[14px]">
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-cyan-100 text-cyan-800 capitalize whitespace-nowrap">
                            {formatStatus(item.data?.status)}
                        </span>
                    </td>
                    <td className="p-4 pr-6 text-[14px] font-medium text-gray-900 sticky right-0 bg-white z-10 shadow-[-6px_0_12px_-12px_rgba(15,23,42,0.35)]">
                        {formatCurrency(item.data?.balance_due, item.data?.currency || 'USD')}
                    </td>
                </tr>
            ));
        }

        return (safeItems as QboBillRecord[]).map((item, index) => (
            <tr key={item._id ?? `bill-${index}`} onClick={() => onRowClick(item)} className="hover:bg-gray-50 transition-colors cursor-pointer group">
                <td className="p-4 text-[14px] font-medium text-gray-500 whitespace-nowrap">{formatDate(item.data?.transaction_date)}</td>
                <td className="p-4 text-[14px] text-gray-900 group-hover:text-indigo-600 transition-colors">
                    <div className="truncate whitespace-nowrap" title={getDocumentVendorName(item, '—') || '—'}>
                        {getDocumentVendorName(item, '—') || '—'}
                    </div>
                </td>
                <td className="p-4 text-[14px] text-gray-500">
                    <div className="truncate whitespace-nowrap" title={item.data?.bill_id || '—'}>
                        {item.data?.bill_id || '—'}
                    </div>
                </td>
                <td className="p-4 text-[14px] text-gray-500 whitespace-nowrap">{formatDate(item.data?.due_date)}</td>
                <td className="p-4 text-[14px]">
                    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-700 capitalize whitespace-nowrap">
                        {formatStatus(getApBillListStatus(item))}
                    </span>
                </td>
                <td className="p-4 text-[14px] font-medium text-gray-900">
                    {formatCurrency(item.data?.total_amount, item.data?.currency || 'USD')}
                </td>
                <td className="p-4 pr-6 text-[14px] font-medium text-gray-900 sticky right-0 bg-white z-10 shadow-[-6px_0_12px_-12px_rgba(15,23,42,0.35)]">
                    {formatCurrency(item.data?.balance_due, item.data?.currency || 'USD')}
                </td>
            </tr>
        ));
    };

    const renderInventoryRows = () => (
        (safeItems as VendorLineItemRecord[]).map((item, index) => (
            <tr key={item._id ?? `inventory-${index}`} onClick={() => onRowClick(item)} className="hover:bg-gray-50 transition-colors cursor-pointer group">
                <td className="p-4 text-[14px] text-gray-900 truncate group-hover:text-indigo-600 transition-colors" title={formatInventoryDisplayName(item)}>
                    {formatInventoryDisplayName(item)}
                </td>
                <td className="p-4 text-[14px] text-gray-500">
                    <div className="group relative max-w-full">
                        <div className="truncate">{formatInventoryVendors(item)}</div>
                        {formatInventoryVendors(item) !== '—' && (
                            <div className="pointer-events-none absolute left-0 top-full z-30 mt-2 hidden min-w-[280px] max-w-[520px] rounded-md border border-gray-200 bg-white px-3 py-2 text-xs leading-5 text-gray-700 shadow-lg group-hover:block">
                                <div className="font-semibold text-gray-900">Vendors</div>
                                <div className="mt-1 space-y-1">
                                    {getInventoryVendorList(item).map((vendorName, vendorIndex) => (
                                        <div key={`${item._id}-vendor-${vendorIndex}`} className="whitespace-nowrap">
                                            {vendorName}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </td>
                <td className="p-4 text-[14px] font-medium text-gray-900">
                    {formatCurrency(item.most_recent_unit_price ?? item.unit_price, item.currency || 'USD')}
                </td>
                <td className="p-4 text-[14px] font-medium text-gray-900">
                    {formatCurrency(item.lowest_unit_price, item.currency || 'USD')}
                </td>
                <td className="p-4 text-[14px] text-gray-500">{item.observation_count ?? '—'}</td>
                <td className="p-4 pr-6 text-[14px] text-gray-500 sticky right-0 bg-white z-10 shadow-[-6px_0_12px_-12px_rgba(15,23,42,0.35)]">
                    {formatDate(item.most_recent_seen_at)}
                </td>
            </tr>
        ))
    );

    const emptyMessage = view === 'inventory'
        ? 'No inventory line items found matching your filters.'
        : `No ${accountingSource.replace(/_/g, ' ')} records found matching your filters.`;
    const pageStart = total === 0 ? 0 : (page - 1) * pageSize + 1;
    const pageEnd = total === 0 ? 0 : Math.min(page * pageSize, total);
    const handlePageSubmit = () => {
        const parsed = Number(pageInput);
        if (!Number.isFinite(parsed)) {
            setPageInput(String(page));
            setIsEditingPage(false);
            return;
        }
        const nextPage = Math.min(Math.max(Math.trunc(parsed), 1), Math.max(totalPages, 1));
        setPageInput(String(nextPage));
        setIsEditingPage(false);
        if (nextPage !== page) onPageChange(nextPage);
    };

    return (
        <div className="w-full relative min-h-0 flex flex-1 flex-col min-w-0">
            <div className="flex-1 overflow-x-auto overflow-y-auto">
                <table className="w-full text-left border-collapse table-fixed min-w-[1100px] [&_th]:font-inter [&_th]:font-medium [&_th]:text-[14px] [&_th]:leading-[120%] [&_th]:tracking-normal [&_th]:align-middle [&_th:first-child]:pl-6 [&_td:first-child]:pl-6 [&_th:last-child]:pr-6 [&_td:last-child]:pr-6">
                    <thead className="border-b border-gray-200 text-[#4C545F] sticky top-0 z-20 bg-white">
                        {view === 'inventory' ? renderInventoryHeaders() : renderAccountingHeaders()}
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {safeItems.length > 0 && (view === 'inventory' ? renderInventoryRows() : renderAccountingRows())}
                        {safeItems.length === 0 && (
                            <tr>
                                <td colSpan={view === 'inventory' ? 7 : accountingColSpan} className="p-12 text-center text-gray-500">
                                    {hasActiveFilters ? emptyMessage : 'No records available for the selected range.'}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            <div className="sticky bottom-0 z-10 mt-auto flex items-center justify-between px-6 py-3 border-t border-gray-200 bg-white">
                <div className="w-1/3 flex justify-start">
                    <span className="text-xs text-gray-500 font-medium">
                        {pageStart} - {pageEnd} of {String(total).replace(/\B(?=(\d{3})+(?!\d))/g, ',')} results
                    </span>
                </div>

                <div className="w-1/3 flex items-center justify-center gap-4">
                    <button
                        disabled={page === 1}
                        onClick={() => onPageChange(page - 1)}
                        className="w-8 h-6 rounded-md bg-[#F9FAFB] text-gray-600 flex items-center justify-center border border-gray-200 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                    >
                        <ChevronLeft size={16} />
                    </button>

                    <span className="text-sm text-gray-600">
                        Page{' '}
                        {isEditingPage ? (
                            <input
                                type="number"
                                min={1}
                                max={Math.max(totalPages, 1)}
                                value={pageInput}
                                autoFocus
                                onChange={(e) => setPageInput(e.target.value)}
                                onBlur={handlePageSubmit}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') handlePageSubmit();
                                    if (e.key === 'Escape') {
                                        setPageInput(String(page));
                                        setIsEditingPage(false);
                                    }
                                }}
                                className="mx-1 h-6 w-10 border-0 border-b-2 border-gray-400 bg-transparent px-0 text-center text-sm text-gray-600 outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                                aria-label="Current page"
                            />
                        ) : (
                            <button
                                type="button"
                                onClick={() => setIsEditingPage(true)}
                                className="mx-1 min-w-[1.25rem] border-b-2 border-gray-300 px-0.5 text-center text-sm text-gray-600 transition-colors hover:border-gray-500"
                                aria-label={`Edit page number, current page ${page}`}
                                title="Click to jump to a page"
                            >
                                {page}
                            </button>
                        )}{' '}
                        of {totalPages || 1}
                    </span>

                    <button
                        disabled={page === totalPages || totalPages === 0}
                        onClick={() => onPageChange(page + 1)}
                        className="w-8 h-6 rounded-md bg-[#F0F0FF] border border-indigo-100 text-indigo-600 flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed hover:bg-indigo-50 transition-colors"
                    >
                        <ChevronRight size={16} />
                    </button>
                </div>

                <div className="w-1/3" />
            </div>

            {accountingSource === 'gl_code' && selectorAccount && (
                <StandardAccountSelector
                    isOpen={!!selectorAccount}
                    onClose={() => setSelectorAccount(null)}
                    currentAccountId={selectorAccount.mapping?.standard_account_id || null}
                    businessId={businessId}
                    onSelect={(account) => {
                        onUpdateGlMapping?.(selectorAccount.data.account_id || selectorAccount._id, account._id);
                        setSelectorAccount(null);
                    }}
                />
            )}
        </div>
    );
};

export default LedgerDatasetTableShell;
